import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  Search,
  CreditCard,
  Loader2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPayments() {
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchPayments();
  }, []);

  useEffect(() => {
    let filtered = payments;
    
    if (searchTerm) {
      filtered = filtered.filter(payment => 
        payment.session_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        payment.user_id?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(payment => payment.payment_status === statusFilter);
    }
    
    setFilteredPayments(filtered);
  }, [searchTerm, statusFilter, payments]);

  const fetchPayments = async () => {
    try {
      const response = await axios.get(`${API}/admin/payments`, getAuthHeader());
      setPayments(response.data);
      setFilteredPayments(response.data);
    } catch (error) {
      console.error('Error fetching payments:', error);
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return <span className="badge-active px-2 py-1 rounded text-xs font-medium">Paid</span>;
      case 'pending':
      case 'initiated':
        return <span className="badge-pending px-2 py-1 rounded text-xs font-medium">Pending</span>;
      case 'failed':
        return <span className="badge-rejected px-2 py-1 rounded text-xs font-medium">Failed</span>;
      default:
        return <span className="badge-inactive px-2 py-1 rounded text-xs font-medium">{status}</span>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 text-[#C5A059] animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <h1 className="font-['Playfair_Display'] text-3xl font-bold text-[#0E285B] mb-8">
        Payment Transactions
      </h1>

      {/* Filters */}
      <div className="bg-white p-4 rounded-sm border border-slate-200 shadow-sm mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search by session ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="payment-search"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="payment-status-filter">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="initiated">Initiated</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Payments Table */}
      <div className="bg-white rounded-sm border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full admin-table">
            <thead>
              <tr>
                <th className="px-6 py-3 text-left">Session ID</th>
                <th className="px-6 py-3 text-left">Membership Type</th>
                <th className="px-6 py-3 text-left">Amount</th>
                <th className="px-6 py-3 text-left">Status</th>
                <th className="px-6 py-3 text-left">Date</th>
              </tr>
            </thead>
            <tbody>
              {filteredPayments.map((payment, index) => (
                <tr key={payment.id} data-testid={`payment-row-${index}`}>
                  <td className="px-6 py-4 font-mono text-sm">
                    {payment.session_id?.substring(0, 20)}...
                  </td>
                  <td className="px-6 py-4 text-slate-600 capitalize">
                    {payment.membership_type}
                  </td>
                  <td className="px-6 py-4 font-medium">
                    €{payment.amount?.toFixed(2)} {payment.currency?.toUpperCase()}
                  </td>
                  <td className="px-6 py-4">{getStatusBadge(payment.payment_status)}</td>
                  <td className="px-6 py-4 text-sm text-slate-500">
                    {new Date(payment.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredPayments.length === 0 && (
          <div className="text-center py-12">
            <CreditCard className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No payments found</p>
          </div>
        )}
      </div>
    </div>
  );
}
